import React, { useState, useCallback, useRef, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  IconButton,
  Tooltip,
  Typography,
  Chip,
  TextField,
  ClickAwayListener,
} from "@material-ui/core";
import {
  ArrowBack,
  Undo,
  Redo,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Save as SaveIcon,
} from "@material-ui/icons";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { useHistory } from "react-router-dom";

const SAVE_STATUS_MAP = {
  idle: { label: "Salvo", color: "#22c55e", bg: "#f0fdf4" },
  pending: { label: "Pendente", color: "#9ca3af", bg: "#f3f4f6" },
  saving: { label: "Salvando...", color: "#eab308", bg: "#fefce8" },
  saved: { label: "Salvo", color: "#22c55e", bg: "#f0fdf4" },
  error: { label: "Erro", color: "#ef4444", bg: "#fef2f2" },
};

const useStyles = makeStyles((theme) => ({
  root: {
    height: 48,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(0, 1.5),
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
    zIndex: 10,
    gap: theme.spacing(1),
  },
  leftSection: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 0,
    flex: 1,
  },
  centerSection: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  rightSection: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  backBtn: {
    padding: 6,
  },
  flowName: {
    fontSize: "0.9rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
    cursor: "pointer",
    padding: "2px 8px",
    borderRadius: 4,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 260,
    border: "1px solid transparent",
    transition: "border-color 150ms",
    "&:hover": {
      borderColor: theme.palette.divider,
    },
  },
  nameInput: {
    "& .MuiInputBase-input": {
      fontSize: "0.9rem",
      fontWeight: 600,
      padding: "2px 8px",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 4,
    },
    maxWidth: 260,
  },
  statusChip: {
    height: 22,
    fontSize: "0.7rem",
    fontWeight: 600,
    borderRadius: 6,
  },
  separator: {
    width: 1,
    height: 24,
    backgroundColor: theme.palette.divider,
    margin: theme.spacing(0, 0.5),
  },
  iconBtn: {
    padding: 6,
    "&.Mui-disabled": {
      opacity: 0.3,
    },
  },
  saveBtn: {
    padding: 6,
    color: theme.palette.primary.main,
  },
}));

export default function FlowToolbar({
  flowName = "",
  onNameChange,
  saveStatus = "idle",
  onSave,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  onZoomIn,
  onZoomOut,
  onFitView,
  onAutoLayout,
  onToggleCopilot,
}) {
  const classes = useStyles();
  const history = useHistory();
  const [editing, setEditing] = useState(false);
  const [nameValue, setNameValue] = useState(flowName);
  const inputRef = useRef(null);

  useEffect(() => {
    setNameValue(flowName);
  }, [flowName]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleStartEditing = useCallback(() => {
    setNameValue(flowName);
    setEditing(true);
  }, [flowName]);

  const handleFinishEditing = useCallback(() => {
    setEditing(false);
    const trimmed = nameValue.trim();
    if (trimmed && trimmed !== flowName && onNameChange) {
      onNameChange(trimmed);
    }
  }, [nameValue, flowName, onNameChange]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        handleFinishEditing();
      }
      if (e.key === "Escape") {
        setNameValue(flowName);
        setEditing(false);
      }
    },
    [handleFinishEditing, flowName]
  );

  const handleBack = useCallback(() => {
    history.push("/flowbuilders");
  }, [history]);

  const status = SAVE_STATUS_MAP[saveStatus] || SAVE_STATUS_MAP.idle;

  return (
    <div className={classes.root}>
      {/* Left: back, name, status */}
      <div className={classes.leftSection}>
        <Tooltip title="Voltar para fluxos">
          <IconButton
            className={classes.backBtn}
            onClick={handleBack}
            size="small"
          >
            <ArrowBack fontSize="small" />
          </IconButton>
        </Tooltip>

        {editing ? (
          <ClickAwayListener onClickAway={handleFinishEditing}>
            <TextField
              className={classes.nameInput}
              variant="outlined"
              size="small"
              value={nameValue}
              onChange={(e) => setNameValue(e.target.value)}
              onKeyDown={handleKeyDown}
              inputRef={inputRef}
            />
          </ClickAwayListener>
        ) : (
          <Tooltip title="Clique para editar o nome">
            <Typography
              className={classes.flowName}
              onClick={handleStartEditing}
            >
              {flowName || "Sem nome"}
            </Typography>
          </Tooltip>
        )}

        <Chip
          className={classes.statusChip}
          label={status.label}
          size="small"
          style={{
            color: status.color,
            backgroundColor: status.bg,
            border: `1px solid ${status.color}30`,
          }}
        />
      </div>

      {/* Center: undo/redo */}
      <div className={classes.centerSection}>
        <Tooltip title="Desfazer (Ctrl+Z)">
          <span>
            <IconButton
              className={classes.iconBtn}
              onClick={onUndo}
              disabled={!canUndo}
              size="small"
            >
              <Undo fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Refazer (Ctrl+Shift+Z)">
          <span>
            <IconButton
              className={classes.iconBtn}
              onClick={onRedo}
              disabled={!canRedo}
              size="small"
            >
              <Redo fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <div className={classes.separator} />

        {/* Zoom controls */}
        <Tooltip title="Zoom In">
          <IconButton
            className={classes.iconBtn}
            onClick={onZoomIn}
            size="small"
          >
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom Out">
          <IconButton
            className={classes.iconBtn}
            onClick={onZoomOut}
            size="small"
          >
            <ZoomOutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Ajustar a vista">
          <IconButton
            className={classes.iconBtn}
            onClick={onFitView}
            size="small"
          >
            <CenterFocusStrongIcon style={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>

        <div className={classes.separator} />

        <Tooltip title="Auto-organizar nodes">
          <IconButton
            className={classes.iconBtn}
            onClick={onAutoLayout}
            size="small"
          >
            <AccountTreeIcon style={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </div>

      {/* Right: copilot + save */}
      <div className={classes.rightSection}>
        <Tooltip title="AI Copilot">
          <IconButton
            className={classes.iconBtn}
            onClick={onToggleCopilot}
            size="small"
            style={{ color: "#8b5cf6" }}
          >
            <SmartToyIcon style={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>

        <div className={classes.separator} />

        <Tooltip title="Salvar (Ctrl+S)">
          <IconButton
            className={classes.saveBtn}
            onClick={onSave}
            size="small"
          >
            <SaveIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
}
